import { useTranslations } from '@vocab/react';
import {
  OverflowMenu,
  MenuItem,
  MenuItemLink,
  Box,
  useToast,
} from 'braid-design-system';
import { useState } from 'react';

import { COMMUNITY_GUIDLINES } from 'src/core/domain/constants';
import errorTranslations from 'src/core/domain/errors/.vocab';
import { ReportError } from 'src/core/domain/errors/reportErrors';
import { handleAuthCheck } from 'src/core/utils/authUtil';
import { useAnalytics } from 'src/provider/analytics';
import { useAuthStatus } from 'src/provider/auth';
import { useLoginModal } from 'src/provider/loginModal';
import { useOptionDialog } from 'src/provider/optionDialog';
import { useThreadDetail } from 'src/provider/threadDetail';
import {
  reportComment,
  type reportCommentParams,
  reportReply,
  type reportReplyParams,
  reportThread,
} from 'src/services/threadInteractionService';

import ReportDialog from '../../ReportDialog/ReportDialog';

import translations from './.vocab';

import * as styles from './ReportButton.css';

export default () => {
  const showToast = useToast();
  const { t } = useTranslations(translations);
  const { t: errorTranslation } = useTranslations(errorTranslations);

  const { authStatus } = useAuthStatus();
  const { trackAnalyticsInteraction } = useAnalytics();
  const { openLoginDialog } = useLoginModal();
  const {
    threadDetailState: { thread },
  } = useThreadDetail();
  const {
    optionDialogState: { interaction, id },
  } = useOptionDialog();

  const [isOpen, setIsOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');

  const openDialog = () => {
    if (
      handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    ) {
      setIsOpen(true);
    }
  };

  const handleThreadReport = async () => {
    try {
      if (!thread) return;
      if (!reportReason || reportReason === '') {
        throw new ReportError('NO_SELECTION');
      }
      await reportThread({
        id,
        reason: reportReason,
      });
      trackAnalyticsInteraction('ReportClick', {
        threadId: id,
        linkAction: 'report thread',
        linkPosition: 'thread',
        reportReason,
      });
      showToast({
        message: 'Thread reported successfully',
        tone: 'positive',
      });
    } catch (err) {
      const reportError = err instanceof ReportError ? err : new ReportError();
      const message = errorTranslation(reportError.key);
      showToast({
        message,
        tone: 'critical',
      });
    }
  };

  const handleCommentReport = async () => {
    try {
      if (!reportReason || reportReason === '') {
        throw new ReportError('NO_SELECTION');
      }
      const params: reportCommentParams = { id, reason: reportReason };
      await reportComment(params);
      trackAnalyticsInteraction('ReportClick', {
        commentId: id,
        linkAction: 'report comment',
        linkPosition: 'thread comment',
        reportReason,
      });
      showToast({
        message: 'Thread reported successfully',
        tone: 'positive',
      });
    } catch (err) {
      const reportError = err instanceof ReportError ? err : new ReportError();
      const message = errorTranslation(reportError.key);
      showToast({
        message,
        tone: 'critical',
      });
    }
  };

  const handleReplyReport = async () => {
    try {
      if (!reportReason || reportReason === '') {
        throw new ReportError('NO_SELECTION');
      }
      const params: reportReplyParams = { id, reason: reportReason };
      await reportReply(params);
      trackAnalyticsInteraction('ReportClick', {
        replyId: id,
        linkAction: 'report thread reply',
        linkPosition: 'thread reply',
        reportReason,
      });
      showToast({
        message: 'Thread reported successfully',
        tone: 'positive',
      });
    } catch (err) {
      const reportError = err instanceof ReportError ? err : new ReportError();
      const message = errorTranslation(reportError.key);
      showToast({
        message,
        tone: 'critical',
      });
    }
  };
  const report = async () => {
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;

    switch (interaction) {
      case 'thread':
        await handleThreadReport();
        break;
      case 'comment':
        await handleCommentReport();
        break;
      case 'reply':
        await handleReplyReport();
        break;
      default:
        break;
    }
    closeDialog();
  };

  const closeDialog = () => {
    setIsOpen(false);
    setReportReason('');
  };

  return (
    <>
      {isOpen && (
        <Box className={styles.mobileContainer}>
          <ReportDialog
            id="report-dialog"
            title={
              interaction === 'thread'
                ? t('reportThreadTitle')
                : t('reportCommentTitle')
            }
            open={isOpen}
            value={reportReason}
            onChange={({ currentTarget: { value } }) => setReportReason(value)}
            onClose={closeDialog}
            onSubmit={report}
          />
        </Box>
      )}
      {!isOpen && (
        <Box
          className={styles.mobileContainer}
          id="options-dropdown-menu-button"
        >
          <OverflowMenu label="Options">
            <MenuItem onClick={openDialog}>{t('reportButton')}</MenuItem>
            <MenuItemLink href={COMMUNITY_GUIDLINES} target="_blank">
              {t('guidelinesButoon')}
            </MenuItemLink>
          </OverflowMenu>
        </Box>
      )}
    </>
  );
};
